import React from 'react';
import { Company } from '../../../../../types/company';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { Collapsable } from '../../../Collapsable';
import { FeatureToggleItem, FeatureToggleTitle, FeatureToggleExplanation, FeatureToggleInformation } from '../common';
import { Button, ButtonVariant } from '../../../../components/Button';
import { useNavigate } from 'react-router-dom';
import { Routing } from '../../../../../util/routes.util';

interface ProductTransactionsFeatureTogglesViewProps {
  company: Company;
  update: (company: Company) => Promise<void>;
}

export default function ProductTransactionsFeatureTogglesView({
  company,
  update,
}: ProductTransactionsFeatureTogglesViewProps) {
  const copy = new Company(company);
  const toggles = copy.settings.featureToggles.productTransactions;

  const navigate = useNavigate();

  return (
    <Collapsable
      open={toggles.productTransactions}
      header={
        <FeatureToggleItem
          value={toggles.productTransactions}
          onChange={async value => await update(copy.withProductTransactionsProductTransactionFeatureToggle(value))}
        >
          <FeatureToggleTitle
            text={t(TranslationKey.productTransactionProductTransactionFeatureToggleTitle).singular.upper}
          />
          <FeatureToggleExplanation
            text={t(TranslationKey.productTransactionProductTransactionFeatureToggleExplanation).singular.upper}
          />
          <FeatureToggleInformation
            text={t(TranslationKey.productTransactionProductTransactionFeatureToggleInformation).singular.upper}
          />
        </FeatureToggleItem>
      }
    >
      <FeatureToggleItem
        indent={1}
        value={toggles.web}
        onChange={async value => await update(copy.withWebProductTransactionFeatureToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.productTransactionWebFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.productTransactionWebFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>
      <FeatureToggleItem
        indent={1}
        value={toggles.mobile}
        onChange={async value => await update(copy.withMobileProductTransactionFeatureToggle(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.productTransactionMobileFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.productTransactionMobileFeatureToggleExplanation).singular.upper}
        />
      </FeatureToggleItem>
      <FeatureToggleItem
        indent={1}
        value={copy.settings.featureToggles.scan.scanRequired || false}
        onChange={async value => await update(copy.withScanRequired(value))}
      >
        <FeatureToggleTitle text={t(TranslationKey.productTransactionScanRequiredFeatureToggleTitle).singular.upper} />
        <FeatureToggleExplanation
          text={t(TranslationKey.productTransactionScanRequiredFeatureToggleExplanation).singular.upper}
        />
        {copy.settings.featureToggles.scan.scanRequired && (
          <Button
            className='mt-2'
            variant={ButtonVariant.primary}
            text={t(TranslationKey.productTransactionScanRequiredWhitelistManageUsers).singular.upper}
            onClick={e => {
              e.stopPropagation();
              navigate(Routing.settings.featureToggles.productTransactionsScanRequiredWhitelistedUsers());
            }}
          />
        )}
      </FeatureToggleItem>
    </Collapsable>
  );
}
